<section class="mobile-dashboard-shell" [attr.lang]="language()">
  <header class="mobile-navbar">
    <div class="navbar-brand">
      <img src="logo.png" alt="Company logo" class="navbar-logo" loading="lazy" decoding="async" />
    </div>
    <div class="navbar-actions">
      <button type="button" class="icon-btn cart-btn" (click)="goToCart()" [attr.aria-label]="t('cart')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <circle cx="8" cy="21" r="1"></circle>
          <circle cx="19" cy="21" r="1"></circle>
          <path d="M2.05 2.05h2l2.66 12.39a2 2 0 0 0 2 1.56h9.74a2 2 0 0 0 2-1.64l1.65-8.42H5.12"></path>
        </svg>
        <span class="icon-btn-label">{{ t('cart') }}</span>
        <span class="cart-count-badge" [attr.aria-label]="cartCount() + ' items'">{{ cartCount() }}</span>
      </button>
      <button type="button" class="icon-btn logout-btn" (click)="logout()" [attr.aria-label]="t('logout')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
          <polyline points="16 17 21 12 16 7"></polyline>
          <line x1="21" y1="12" x2="9" y2="12"></line>
        </svg>
        <span class="icon-btn-label">{{ t('logout') }}</span>
      </button>
    </div>
  </header>

  <section class="mobile-topbar">
    <div class="topbar-row">
      <p>{{ t('hello') }}: {{ inferredDisplayName() }}</p>
      <p class="tier-pill">{{ t('tier') }}: {{ tier() | titlecase }}</p>
    </div>
  </section>

  @if (categoryOptions().length > 0) {
    <section class="category-filter-wrap" [attr.aria-label]="t('filterLabel')">
      <div class="category-filter-head">
        <span>{{ t('filterLabel') }}</span>
        <div class="filter-head-actions">
          @if (selectedCategoryIDs().length > 0) {
            <button type="button" class="clear-filter-btn" (click)="clearCategoryFilters()">{{ t('clearFilters') }}</button>
          }
          <button
            type="button"
            class="toggle-filter-btn"
            [attr.aria-label]="categoryFilterOpen() ? t('hideFilters') : t('showFilters')"
            [attr.title]="categoryFilterOpen() ? t('hideFilters') : t('showFilters')"
            (click)="toggleCategoryFilterPanel()"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M3 6h18"></path>
              <path d="M6 12h12"></path>
              <path d="M10 18h4"></path>
            </svg>
          </button>
        </div>
      </div>

      @if (categoryFilterOpen()) {
        <div class="category-chip-grid">
          @for (category of categoryOptions(); track category.id) {
            <button
              type="button"
              class="category-chip"
              [class.active]="isCategorySelected(category.id)"
              [disabled]="categoryLoading()"
              (click)="toggleCategoryFilter(category.id)"
            >
              {{ categoryName(category) }}
            </button>
          }
        </div>
      }
    </section>
  }

  @if (error()) {
    <p class="error-note">{{ error() }}</p>
  }

  @if (categoryLoading()) {
    <p class="loading-note">{{ t('loading') }}</p>
  } @else if (loading() && products().length === 0) {
    <p class="loading-note">{{ t('loading') }}</p>
  } @else {
    <div class="products-table-wrap desktop-only">
      <table class="products-table">
        <thead>
          <tr>
            <th class="col-image">{{ t('image') }}</th>
            <th class="col-product">{{ t('product') }}</th>
            <th class="col-categories">{{ t('categoriesLabel') }}</th>
            <th class="col-qty">{{ t('quantity') }}</th>
            <th class="col-ranges">{{ t('ranges') }}</th>
            <th class="col-total">{{ t('lineTotal') }}</th>
            <th class="col-action">{{ t('cart') }}</th>
          </tr>
        </thead>
        <tbody>
          @for (product of visibleProducts(); track trackByProductID($index, product)) {
            <tr>
              <td class="col-image">
                <img [src]="firstImage(product)" [alt]="productName(product)" loading="lazy" decoding="async" />
              </td>
              <td class="col-product">
                <div class="cell-title">{{ productName(product) }}</div>
                <div class="cell-desc">{{ productDescription(product) }}</div>
              </td>
              <td class="col-categories">
                <div class="cell-categories">{{ categoryList(product) }}</div>
              </td>
              <td class="col-qty">
                <div class="qty-meta">
                  <input
                    type="number"
                    [min]="packageSize(product)"
                    [step]="packageSize(product)"
                    max="99999"
                    class="qty-input"
                    [value]="getSelectedQuantity(product)"
                    (input)="setSelectedQuantity(product, $any($event.target).value)"
                  />
                  <div class="unit-price">{{ t('packIncrementHint') }} {{ packageSize(product) }}</div>
                    <div class="unit-price">{{ unitPriceForProduct(product) | moneyComma }} {{ t('perUnit') }}</div>
                </div>
              </td>
              <td class="col-ranges">
                @if (hasPriceRanges(product)) {
                  <div class="price-list">
                    @for (range of product.priceRanges; track trackByRange($index, range)) {
                      <div class="price-row">
                        <span class="price-range-qty">{{ range.start }}-{{ range.end }}</span>
                          <strong class="price-range-value">{{ tierPrice(range) | moneyComma }}</strong>
                      </div>
                    }
                  </div>
                } @else {
                  <span>-</span>
                }
              </td>
              <td class="col-total">
                  <strong class="line-total">{{ lineTotalForProduct(product) | moneyComma }}</strong>
              </td>
              <td class="col-action">
                <div class="action-stack">
                  <button type="button" class="details-btn" (click)="openProductDetails(product)">{{ t('details') }}</button>
                  <button type="button" (click)="addToCart(product)">{{ t('order') }}</button>
                </div>
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>

    <div class="mobile-cards mobile-only">
      @for (product of visibleProducts(); track trackByProductID($index, product)) {
        <article class="product-card">
          <img [src]="firstImage(product)" [alt]="productName(product)" loading="lazy" decoding="async" />

          <div class="product-card-body">
            <h3>{{ productName(product) }}</h3>
            <p>{{ productDescription(product) }}</p>

            <div class="meta-row">
              <span class="meta-label">{{ t('categoriesLabel') }}</span>
              <span class="meta-value">{{ categoryList(product) }}</span>
            </div>

            <div class="meta-row">
              <span class="meta-label">{{ t('quantity') }}</span>
              <div class="qty-meta">
                <input
                  type="number"
                  [min]="packageSize(product)"
                  [step]="packageSize(product)"
                  max="99999"
                  class="qty-input"
                  [value]="getSelectedQuantity(product)"
                  (input)="setSelectedQuantity(product, $any($event.target).value)"
                />
                <div class="unit-price">{{ t('packIncrementHint') }} {{ packageSize(product) }}</div>
                <div class="unit-price">{{ unitPriceForProduct(product) | moneyComma }} {{ t('perUnit') }}</div>
              </div>
            </div>

            @if (hasPriceRanges(product)) {
              <div class="price-list card-price-list">
                @for (range of product.priceRanges; track trackByRange($index, range)) {
                  <div class="price-row">
                    <span class="price-range-qty">{{ range.start }}-{{ range.end }}</span>
                    <strong class="price-range-value">{{ tierPrice(range) | moneyComma }}</strong>
                  </div>
                }
              </div>
            }

            <div class="line-total-wrap">
              <span>{{ t('lineTotal') }}</span>
              <strong class="line-total">{{ lineTotalForProduct(product) | moneyComma }}</strong>
            </div>

            <button type="button" class="card-details-btn" (click)="openProductDetails(product)">{{ t('details') }}</button>
            <button type="button" class="card-order-btn" (click)="addToCart(product)">{{ t('order') }}</button>
          </div>
        </article>
      }
    </div>

    @if (canLoadMore()) {
      <div class="load-more-wrap">
        <button type="button" (click)="loadMore()">{{ t('loadMore') }}</button>
      </div>
    }
  }
</section>
